import React, { useMemo, useState } from "react";
import styles from "./header-buttons-icons.module.scss";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import classnames from "classnames";
import { Loader } from "@whatslly/ui-components/Loader";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { EntityFilter } from "@whatslly/ui-components/EntityFilter";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import type { UserManagementUser } from "../../types/UserManagementUser";
import { UserManagementStore } from "../../stores/UserManagementStore";
import { FilterButton } from "@whatslly/ui-components/FilterButton";
import { granularPermissionDomains } from "@whatslly/commons/platformPermissions";
import type { PermissionInstanceConfigs } from "@whatslly/commons/platformPermissions";

const GRANULAR_PERMISSIONS_I18N_PREFIX = "user_management.invite_user_modal.granular_permissions";

export const HeaderButtonsIcons: React.FC<{
  users: UserManagementUser[];
  instanceConfigs?: PermissionInstanceConfigs;
  findDuplicates?: { onClick: () => void; tooltip: string; loading?: boolean };
}> = observer(({ users, instanceConfigs, findDuplicates }) => {
  const { t } = useTranslation();
  const store = useDependencyInjector(UserManagementStore);
  const [searchText, setSearchText] = useState("");
  const groupNames = useMemo(
    () => store.groupNames?.case({ fulfilled: (groupNames) => Object.fromEntries(groupNames.map((g) => [g._id, g.name])) }),
    [store.groupNames]
  );

  const permissionLabels = useMemo(() => {
    const permissionLabel = (instanceConfig: string, key: string) =>
      `${t(`${GRANULAR_PERMISSIONS_I18N_PREFIX}.${instanceConfig}`)} - ${t(`${GRANULAR_PERMISSIONS_I18N_PREFIX}.${key}`)}`;

    const labeledEntries = Object.entries(granularPermissionDomains)
      .filter(([instanceConfig]) => !instanceConfigs || instanceConfigs[instanceConfig as keyof PermissionInstanceConfigs])
      .flatMap(([instanceConfig, keys]) =>
        keys.filter((key) => !key.endsWith(".all")).map((key) => [key, permissionLabel(instanceConfig, key)] as const)
      )
      .sort(([, labelA], [, labelB]) => labelA.localeCompare(labelB));

    return Object.fromEntries(labeledEntries);
  }, [t, instanceConfigs]);

  return (
    <div className={styles.headerButtonsIcons}>
      <Tooltip delay={300} value={t("user_management.download_csv")} placement="bottom">
        <div
          data-testid={`download-users-csv`}
          onClick={() => store.downloadCsvFile()}
          className={classnames(styles.headerButton, { [styles.disabled]: store.downloadCsvResult?.state === "pending" })}
        >
          {store.downloadCsvResult?.state === "pending" ? <Loader scale={0.25} /> : <SvgV2 width={20} height={20} iconName={"download"} />}
        </div>
      </Tooltip>
      {findDuplicates && (
        <Tooltip delay={300} value={findDuplicates.tooltip} placement="bottom">
          <div
            data-testid={`find-duplicates`}
            onClick={() => !findDuplicates.loading && findDuplicates.onClick()}
            className={classnames(styles.headerButton, { [styles.disabled]: findDuplicates.loading })}
          >
            {findDuplicates.loading ? <Loader scale={0.25} /> : <SvgV2 width={20} height={20} iconName={"people"} />}
          </div>
        </Tooltip>
      )}
      <EntityFilter
        title={t("user_management.filters.title")}
        onSelectedFiltersChanged={(filters) => {
          store.setFilters({ filterGroups: filters.groupIds ?? [], filterPermissions: filters.permissions ?? [] });
        }}
        initFilteredFields={{ groupIds: store.filterGroups, permissions: store.filterPermissions }}
        useSearchInput={() => ({ searchText, setSearchText })}
        filterSettings={[
          ...(groupNames
            ? [
                {
                  title: t("user_management.cols.groups"),
                  searchKey: "groupIds",
                  customObjectLabel: groupNames,
                },
              ]
            : []),
          {
            title: t("user_management.filters.permissions"),
            searchKey: "permissions",
            customObjectLabel: permissionLabels,
          },
        ]}
        BooleanFilters={[
          {
            text: t("user_management.filters.hide_users"),
            onChange: (newState: boolean) => {
              store.hideInactiveUsers = newState;
            },
            value: store.hideInactiveUsers,
            defaultValue: true,
          },
          {
            text: t("user_management.filters.restrict_to_direct_reports"),
            onChange: (newState: boolean) => {
              store.onlyShowRelatedToMe = newState;
            },
            value: store.onlyShowRelatedToMe,
            disabled: store.restrictToDirectReports,
            defaultValue: false,
          },
          {
            text: t("user_management.filters.online_only"),
            onChange: (newState: boolean) => {
              store.onlineOnly = newState;
            },
            value: store.onlineOnly,
            defaultValue: false,
          },
        ]}
        entities={users}
        EntityFilterButton={FilterButton}
        oppositeCheckedPosition={true}
      />
    </div>
  );
});
